// context/AuthContext.js
'use client'

import { createContext, useContext, useState, useEffect, useCallback } from 'react'
import LoginModal from '@/components/loginModal'
import { createClientService } from '@/lib/http/client'

// 1. 定义状态类型
const DataContext = createContext({
    user: null,
    login: () => { },
    logout: () => { },
    openLogin: () => { },
    closeLogin: () => { },
    showLoginModal: false,
    isLogin: false,
})

// 2. 创建 Provider 组件
export function DataProvider({ children, data }) {
    const [user, setUser] = useState(null)
    const [showLoginModal, setShowLogin] = useState(false)
    const [isLogin, setIsLogin] = useState(false);


    const [loginResolve, setLoginResolve] = useState(null);


    // 组件挂载后读取 localStorage 加载用户数据
    useEffect(() => {
        const savedState = localStorage.getItem('showLogin') === 'true'
        setShowLogin(savedState)

        const savedUser = localStorage.getItem('user')
        if (savedUser) {
            setUser(JSON.parse(savedUser))
        }

        loginValidate();

    }, [])

    // 添加登录成功回调机制
    const openLogin = useCallback((onSuccess, onCancel) => {

        // 修改：支持取消回调
        setLoginResolve(() => {
            return (result) => {
                if (result instanceof Error) onCancel?.(result);
                else onSuccess?.();
            };
        });

        setShowLogin(true);
        if (typeof window !== 'undefined') {
            localStorage.setItem('showLogin', 'true');
        }
    }, []);

    const closeLogin = useCallback(() => {
        setShowLogin(false);
        if (loginResolve) {
            loginResolve(new Error('Login canceled by user'));
            setLoginResolve(null);
        }
        if (typeof window !== 'undefined') {
            localStorage.removeItem('showLogin');
        }
    }, [loginResolve]);

    const login = useCallback((userData) => {
        setUser(userData);
        setIsLogin(true);

        // 执行登录成功回调
        if (typeof loginResolve === 'function') {
            loginResolve();
            setLoginResolve(null);
        }

        setShowLogin(false);
        localStorage.setItem('user', JSON.stringify(userData));
        localStorage.removeItem('showLogin');
    }, [loginResolve]);


    const loginValidate = async () => {

        try {
            await createClientService().get('mtop.login.validate')
            const personalData = await createClientService().get('mtop.user.getUserPersonalData');
            let userData = {
                nickname: personalData?.baseInfo?.nickname || '',
                iconUrl: personalData?.baseInfo?.iconUrl || '',
            }
            login(userData);
            setIsLogin(true)
        } catch (error) {
            console.log("🚀 ~ loginValidate ~ error:", error)
            logout();
        }
    }

    const logout = useCallback(() => {
        setUser(null)
        setIsLogin(false);
        localStorage.removeItem('user');
        localStorage.removeItem('showLogin');
        localStorage.removeItem('mat');
        document.cookie = 'mat=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
        // 清除所有等待的登录请求
        setLoginResolve(null);
    }, [])

    return (
        <DataContext.Provider value={{ user, login, logout, openLogin, closeLogin, showLoginModal, data, isLogin, setIsLogin }}>
            {children}
            {showLoginModal && <LoginModal onClose={closeLogin} showLoginModal={showLoginModal} />}
        </DataContext.Provider>
    )
}

// 3. 创建自定义 Hook（带错误处理）
export const useData = () => {
    const context = useContext(DataContext)
    if (!context) {
        throw new Error('useData must be used within an DataProvider')
    }
    return context
}